import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { dashboardApi } from "~/feature/dashboard/dashboardApi";
import { INITIAL_MARKETS_LIMIT } from "~/feature/event/components/MarketsDataGrid/constants";
import { buildInitialMarketsRequestParams } from "~/feature/event/initialMarketsRequest";
import type { AppDispatch } from "~/store/store";
import type { IMarket, IOutcomeConsensus } from "~/types/event";

/**
 * Puts the SSR-loaded markets into the RTK Query cache under the grid's own
 * cache key, so the grid's first query is a hit and the markets are fetched
 * once — on the server — rather than again on mount.
 *
 * Must be called above `useEventMarkets` in the component: effects run in
 * hook-call order, and this has to land before the query subscribes.
 */
export const useSeedEventMarkets = (
  eventId: string,
  markets: IMarket[],
  outcomes: IOutcomeConsensus[]
) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!markets.length) {
      return;
    }

    dispatch(
      dashboardApi.util.upsertQueryData(
        "getMarketsByEventId",
        buildInitialMarketsRequestParams(eventId),
        // What the API would have answered for that page: the loader's markets
        // are already in the grid's sort order and unfiltered, so its first
        // page is the leading slice and `total` is the full count.
        {
          data: markets.slice(0, INITIAL_MARKETS_LIMIT),
          page: 1,
          limit: INITIAL_MARKETS_LIMIT,
          total: markets.length,
          outcomes,
        }
      )
    );
  }, [dispatch, eventId, markets, outcomes]);
};
